import {DataTestAttrs} from '@benchling/js-test-utils';
import React from 'react';

import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import StructuredTableEditStatusManager from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableEditStatusManager';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook, {
  StructuredTableOutsideNotebookWithTableViews,
} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';

export interface ToggleEditStructuredTableOutsideNotebookProps<T extends UnknownStructuredTable> {
  isEditable: boolean;
  structuredTable: T;
  isMaximized?: boolean;
  disableMaximizedTables?: boolean;
  outsideNotebookContext?: OutsideNotebookContext;
  update: (structuredTable: T) => void;
  updateComputedTable?: (computedTable: Array<Array<EvaluatedCell>>) => void;
  setIsMaximized?: () => void;
  submit: () => Promise<void>;
  revert: (structuredTable: T) => void;
  enableFormulas?: boolean;
  // When true, will use StructuredTableOutsideNotebookWithTableViews instead of StructuredTableOutsideNotebook
  useWithTableViews?: boolean;
  // When true, shows column resize handles. Handles only appear while the table is unlocked, so this
  // makes resizing available in edit mode only. Persisting the resulting widths is the caller's job.
  enableColumnResizing?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function ToggleEditStructuredTableOutsideNotebook<T extends UnknownStructuredTable>(
  props: ToggleEditStructuredTableOutsideNotebookProps<T>
): JSX.Element {
  const StructuredTableComponent = props.useWithTableViews
    ? StructuredTableOutsideNotebookWithTableViews
    : StructuredTableOutsideNotebook;

  return props.isEditable ? (
    <StructuredTableEditStatusManager
      tableCid={props.structuredTable.cid}
      table={props.structuredTable}
      submitHandler={props.submit}
      revertHandler={props.revert}
    >
      {(editStatus: StructuredTableEditStatus) => (
        <StructuredTableComponent
          structuredTable={props.structuredTable}
          cid={props.structuredTable.cid}
          updateHandler={props.update}
          onComputedTableChanged={props.updateComputedTable}
          disableMaximizedTables={props.disableMaximizedTables ?? false}
          isMaximized={props.isMaximized}
          setMaximizedTable={props.setIsMaximized}
          outsideNotebookContext={props.outsideNotebookContext}
          locked={editStatus === StructuredTableEditStatus.LOCKED}
          includeUnsubmittedStatus={editStatus === StructuredTableEditStatus.EDITING}
          enableFormulas={props.enableFormulas}
          enableColumnResizing={props.enableColumnResizing}
          dataTestAttrs={props.dataTestAttrs}
        />
      )}
    </StructuredTableEditStatusManager>
  ) : (
    <StructuredTableComponent
      structuredTable={props.structuredTable}
      cid={props.structuredTable.cid}
      // A non-editable table can still be updated (e.g., by action functions)
      updateHandler={props.update}
      locked={true}
      lockActionFunctions={true}
      disableMaximizedTables={props.disableMaximizedTables ?? false}
      isMaximized={props.isMaximized}
      setMaximizedTable={props.setIsMaximized}
      includeUnsubmittedStatus={false}
      enableFormulas={props.enableFormulas}
      enableColumnResizing={props.enableColumnResizing}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}
